"use client";

import { useEffect, useState } from "react";
import { useRouter, usePathname } from "next/navigation";

interface BrandUser {
  id: string;
  brandName: string;
  brandSlug: string;
  plan: string;
  email: string;
}

export default function BrandLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const [user, setUser] = useState<BrandUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/api/auth/me")
      .then(async (res) => {
        if (!res.ok) throw new Error("Not authenticated");
        const data = await res.json();
        setUser(data);
      })
      .catch(() => {
        router.push("/brand/login");
      })
      .finally(() => setLoading(false));
  }, [router]);

  if (loading) {
    return (
      <div className="brand-loading">
        <div className="brand-loading__spinner" />
      </div>
    );
  }

  if (!user) return null;

  const navItems = [
    { label: "Dashboard", href: "/brand/dashboard", icon: "▦" },
    { label: "Products", href: "/brand/products", icon: "▤" },
    { label: "Orders", href: "/brand/orders", icon: "▥" },
    { label: "Settings", href: "/brand/settings", icon: "⚙" },
  ];

  const isActive = (href: string) => {
    if (href === "/brand/dashboard") return pathname === "/brand/dashboard";
    return pathname.startsWith(href);
  };

  return (
    <div className="brand-shell">
      <aside className="brand-sidebar">
        <div className="brand-sidebar__logo">
          <span className="brand-sidebar__logo-text">DressingRoom</span>
          <span className="brand-sidebar__logo-sub">for Brands</span>
        </div>

        <nav className="brand-sidebar__nav">
          {navItems.map((item) => (
            <a
              key={item.label}
              href={item.href}
              className={`brand-sidebar__link ${
                isActive(item.href) ? "brand-sidebar__link--active" : ""
              }`}
            >
              <span className="brand-sidebar__icon">{item.icon}</span>
              <span>{item.label}</span>
            </a>
          ))}
        </nav>

        <div className="brand-sidebar__footer">
          <a
            href={`/store/${user.brandSlug}`}
            className="brand-sidebar__store-link"
          >
            View Store →
          </a>
        </div>
      </aside>

      <div className="brand-main">
        <header className="brand-topbar">
          <div className="brand-topbar__left">
            <h2 className="brand-topbar__brand-name">{user.brandName}</h2>
            <span className="brand-topbar__plan-badge">{user.plan || "Free"}</span>
          </div>
          <div className="brand-topbar__right">
            <a
              href={`/store/${user.brandSlug}`}
              className="brand-topbar__link"
            >
              View Store
            </a>
            <button
              className="brand-topbar__logout"
              onClick={async () => {
                await fetch("/api/auth/logout", { method: "POST" });
                router.push("/brand/login");
              }}
            >
              Log Out
            </button>
          </div>
        </header>

        <div className="brand-content">{children}</div>
      </div>

      {/* Mobile bottom nav */}
      <nav className="brand-mobile-nav">
        {navItems.map((item) => (
          <a
            key={item.href}
            href={item.href}
            className={`brand-mobile-nav__btn ${isActive(item.href) ? "brand-mobile-nav__btn--active" : ""}`}
          >
            <span className="brand-mobile-nav__icon">{item.icon}</span>
            <span>{item.label}</span>
          </a>
        ))}
      </nav>
    </div>
  );
}
